<template>
  <base-sheet :visible="visible" aria-label="Escolher módulos para baixar" @close="onCancel">
    <h3 class="vxl-sheet__title">Baixar curso</h3>
    <p class="vxl-sheet__subtitle">{{ subtitle }}</p>

    <!-- Abas só quando o curso tem materiais complementares: com filesCount 0 a
         sheet fica exatamente como sempre foi. -->
    <div v-if="hasMaterials" class="sheet-tabs" role="tablist">
      <button
        type="button"
        role="tab"
        class="sheet-tab"
        :class="{ active: tab === 'curso' }"
        :style="tab === 'curso' ? activeTabStyle : null"
        :aria-selected="tab === 'curso'"
        @click="tab = 'curso'"
      >Aulas</button>
      <button
        type="button"
        role="tab"
        class="sheet-tab"
        :class="{ active: tab === 'materiais' }"
        :style="tab === 'materiais' ? activeTabStyle : null"
        :aria-selected="tab === 'materiais'"
        @click="openMaterials"
      >Materiais <span class="sheet-tab-count">{{ filesCount }}</span></button>
    </div>

    <div v-if="tab === 'curso'" class="list-head">
      <button
        v-if="!isBusy && selectable.length > 1"
        type="button"
        class="select-all"
        @click="toggleAll"
      >
        {{ allSelected ? 'Limpar seleção' : 'Selecionar todos' }}
      </button>
      <span class="total">{{ groups.length }} módulos</span>
    </div>

    <div v-if="tab === 'curso'" class="modules" role="group">
      <button
        v-for="group in groups"
        :key="group.id"
        type="button"
        class="module-btn"
        :class="{ done: group.done || isBusy }"
        :disabled="group.done || isBusy"
        role="checkbox"
        :aria-checked="group.done || isSelected(group.id)"
        @click="toggle(group.id)"
      >
        <span
          class="box"
          :style="boxStyle(group)"
        >
          <i v-if="group.done || isSelected(group.id)" class="material-icons">check</i>
        </span>
        <span class="text">
          <span class="name">{{ group.className }}</span>
          <span class="meta">{{ groupMeta(group) }}</span>
        </span>
      </button>
    </div>

    <!-- Materiais: a mesma lista do slideover de Downloads, dentro do mesmo
         container rolável da lista de módulos. Baixar material é ação imediata
         e independente — não resolve a Promise da sheet. -->
    <div v-if="tab === 'materiais'" class="modules materials">
      <course-files-list v-if="drmReady" ref="filesList" />
      <p v-else-if="drmFailed" class="materials-fallback">
        Não foi possível carregar os materiais. Verifique a conexão e tente de novo.
      </p>
      <p v-else class="materials-fallback">Carregando materiais…</p>
    </div>

    <button
      v-if="tab === 'curso'"
      type="button"
      class="vxl-sheet__action vxl-sheet__action--primary"
      :disabled="confirmDisabled"
      :style="{ background: $theme.primaryColor }"
      @click="onConfirm"
    >
      {{ confirmLabel }}
    </button>
    <button
      type="button"
      class="vxl-sheet__action vxl-sheet__action--ghost"
      @click="onCancel"
    >
      {{ tab === 'materiais' || isBusy ? 'Fechar' : 'Cancelar' }}
    </button>
  </base-sheet>
</template>

<script>
import BaseSheet from '@/components/base/BaseSheet';
import CourseFilesList from '@/components/CourseFilesList';

// Seleção de módulos do "Baixar curso". O botão não dispara o download direto
// porque curso inteiro é a ação mais cara do app — dezenas de GB — e o aluno
// merece ver o escopo antes de se comprometer. De quebra atende o caso real de
// "só quero os dois módulos da viagem".
//
// Módulo já baixado entra na lista marcado e desabilitado: some da conta do
// botão (não há o que baixar) mas continua visível, senão o aluno procura um
// módulo que sabe que existe e não acha.
export default {
  name: 'DownloadModulesSheet',
  components: { BaseSheet, CourseFilesList },
  props: {
    visible: { type: Boolean, default: false },
    // [{ id, className, learningObjectIds: [], done: Boolean }]
    groups: { type: Array, default: () => [] },
    // Aba "Materiais": filesCount > 0 liga as abas; courseId alimenta o fetch
    // do DRM de PDF que a lista de materiais exige antes de qualquer download.
    courseId: { type: [Number, String], default: null },
    filesCount: { type: Number, default: 0 },
    // Estado agregado do curso. A sheet abre em qualquer um deles porque no app
    // ela é o único caminho para os materiais complementares — o atalho antigo,
    // que ia direto para a confirmação de remover/cancelar, tornava os
    // materiais inalcançáveis assim que o curso terminava de baixar.
    status: { type: String, default: 'idle' }
  },
  data () {
    return {
      selected: [],
      tab: 'curso',
      // O getDRM engole erro internamente (toast) e resolve mesmo assim, então
      // o "carregou" é a presença do objeto no store — não o settle do dispatch.
      drmFailed: false
    };
  },
  computed: {
    hasMaterials () {
      return this.filesCount > 0;
    },
    isBusy () {
      return this.status !== 'idle';
    },
    subtitle () {
      if (this.tab === 'materiais') return 'Arquivos complementares do curso.';
      if (this.status === 'downloading') return 'Download em andamento.';
      if (this.status === 'downloaded') return 'Todo o conteúdo está disponível offline.';
      return 'Escolha o que levar para o modo offline.';
    },
    activeTabStyle () {
      return { borderColor: this.$theme.primaryColor, color: '#fff' };
    },
    drmReady () {
      return !!this.$store.state.learningObject.drm;
    },
    selectable () {
      return this.groups.filter(g => !g.done);
    },
    allSelected () {
      return this.selectable.length > 0 && this.selected.length === this.selectable.length;
    },
    selectedGroups () {
      return this.selectable.filter(g => this.selected.indexOf(g.id) !== -1);
    },
    selectedLessonCount () {
      return this.selectedGroups.reduce((n, g) => n + (g.learningObjectIds || []).length, 0);
    },
    confirmLabel () {
      if (this.status === 'downloading') return 'Cancelar download';
      if (this.status === 'downloaded') return 'Remover downloads';
      const n = this.selectedLessonCount;
      if (!n) return 'Baixar';
      return n === 1 ? 'Baixar 1 aula' : `Baixar ${n} aulas`;
    },
    // Só o caminho de baixar depende de haver seleção; cancelar e remover valem
    // para o curso inteiro.
    confirmDisabled () {
      return !this.isBusy && !this.selectedLessonCount;
    },
    confirmAction () {
      if (this.status === 'downloading') return 'cancel';
      if (this.status === 'downloaded') return 'remove';
      return 'download';
    }
  },
  watch: {
    // Todos marcados por padrão, e re-marcados a cada abertura: a sheet é
    // reusada entre cursos, então manter a seleção anterior mostraria um estado
    // que não é deste curso.
    visible: {
      immediate: true,
      handler (now) {
        if (now) {
          this.selected = this.selectable.map(g => g.id);
          // A sheet é reusada entre cursos: aba anterior não pode vazar.
          this.tab = 'curso';
          this.drmFailed = false;
        }
      }
    }
  },
  methods: {
    isSelected (id) {
      return this.selected.indexOf(id) !== -1;
    },
    toggle (id) {
      const i = this.selected.indexOf(id);
      if (i === -1) this.selected.push(id);
      else this.selected.splice(i, 1);
    },
    toggleAll () {
      this.selected = this.allSelected ? [] : this.selectable.map(g => g.id);
    },
    // Com download em curso ou concluído a lista é informativa: mostra o que há,
    // sem oferecer uma seleção que não muda nada.
    groupMeta (group) {
      if (group.done) return 'Já baixado';
      if (this.status === 'downloading') return 'Na fila';
      return this.lessonLabel(group);
    },
    lessonLabel (group) {
      const n = (group.learningObjectIds || []).length;
      return n === 1 ? '1 aula' : `${n} aulas`;
    },
    boxStyle (group) {
      const on = group.done || this.isSelected(group.id);
      if (!on) return null;
      return { background: this.$theme.primaryColor, borderColor: this.$theme.primaryColor };
    },
    async openMaterials () {
      this.tab = 'materiais';
      if (this.drmReady || !this.courseId) return;
      await this.$store.dispatch('learningObject/getDRM', this.courseId);
      if (!this.drmReady) this.drmFailed = true;
    },
    // Emite AÇÃO + payload em vez de só a seleção: a sheet agora atende os três
    // estados do curso, e quem chamou precisa saber se deve baixar, cancelar ou
    // remover.
    onConfirm () {
      this.$emit('confirm', { action: this.confirmAction, groups: this.selectedGroups });
    },
    onCancel () {
      // Material em transferência: fechar agora perderia o arquivo no meio —
      // mesmo guard do slideover de Downloads.
      const list = this.$refs.filesList;
      if (list && list.hasActive && list.hasActive()) {
        this.$store.commit('toast/NEW', { message: 'Aguarde os downloads', duration: 1000 });
        return;
      }
      this.$emit('cancel');
    }
  }
};
</script>

<style scoped>
/* `sheet-tabs`/`sheet-tab` e não `tabs`/`tab`: são classes do Materialize, que
   é global aqui. A primeira versão usou os nomes curtos e o componente de abas
   dele pintou fundo branco e um indicador azul por cima — o scoped não protege
   contra regra global que casa com a mesma classe. */
.sheet-tabs {
  display: flex;
  width: 100%;
  gap: 18px;
  margin-bottom: 12px;
  border-bottom: 0.5px solid rgba(255, 255, 255, 0.12);
}

.sheet-tab {
  background: none;
  border: none;
  border-bottom: 2px solid transparent;
  padding: 6px 2px 8px;
  font-size: 14px;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.55);
  cursor: pointer;
}

.sheet-tab-count {
  font-size: 11px;
  opacity: 0.7;
}

/* O container reaproveita .modules (mesmo fundo, raio e scrollbar); só o
   padding sai, porque a lista de materiais traz os espaçamentos dela. */
.materials {
  padding: 0;
}

.materials-fallback {
  padding: 18px 14px;
  font-size: 13px;
  color: rgba(255, 255, 255, 0.6);
}

.select-all {
  align-self: flex-start;
  background: none;
  border: none;
  padding: 4px 0 10px;
  font-size: 13px;
  text-decoration: underline;
  cursor: pointer;
  color: rgba(255, 255, 255, 0.75);
}

.list-head {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  width: 100%;
  padding-bottom: 8px;
}

.total {
  font-size: 12px;
  color: rgba(255, 255, 255, 0.55);
}

/* Fundo próprio + barra de rolagem sempre reservada. Sem isso a lista terminava
   rente ao botão primário e lia como se acabasse no módulo visível — num curso
   de 18 módulos o aluno não tinha como saber que havia mais. `scroll` em vez de
   `auto` porque o WKWebView esconde a barra overlay quando parada, que é
   exatamente quando o aluno precisa da dica. */
.modules {
  width: 100%;
  max-height: 42vh;
  overflow-y: scroll;
  -webkit-overflow-scrolling: touch;
  margin-bottom: 14px;
  padding: 2px 6px 2px 10px;
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.04);
  border: 0.5px solid rgba(255, 255, 255, 0.08);
}

.modules::-webkit-scrollbar {
  width: 5px;
}

.modules::-webkit-scrollbar-thumb {
  background: rgba(255, 255, 255, 0.28);
  border-radius: 3px;
}

.modules::-webkit-scrollbar-track {
  background: transparent;
}

.module-btn + .module-btn {
  border-top: 0.5px solid rgba(255, 255, 255, 0.07);
}

.module-btn {
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  padding: 10px 2px;
  background: none;
  border: none;
  text-align: left;
  cursor: pointer;
  /* Explícita, não `inherit`: herdando, a linha pegava a cor do body do app e
     ficava quase do tom do fundo da lista. Também é o currentColor da borda do
     checkbox desmarcado. */
  color: rgba(255, 255, 255, 0.92);
  margin-right: 0;
}

.module-btn.done {
  opacity: 0.55;
  cursor: default;
}

.box {
  flex-shrink: 0;
  width: 22px;
  height: 22px;
  border-radius: 6px;
  border: 2px solid currentColor;
  display: flex;
  align-items: center;
  justify-content: center;
}

.box .material-icons {
  font-size: 16px;
  color: #fff;
}

.text {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.name {
  font-size: 14px;
  line-height: 1.3;
  font-weight: 500;
}

/* Cor em vez de opacity: opacity multiplica contra o fundo da lista e o
   resultado muda conforme a linha esteja sobre o container ou não. */
.meta {
  font-size: 12px;
  color: rgba(255, 255, 255, 0.6);
  margin-top: 2px;
}
</style>
